import { resolve, normalize, isAbsolute, relative } from 'path'
import { z } from 'zod'
import { ok, err, createCoreError, type Result, type CoreError } from '@trailhead/core'
import type { ProjectConfig, PackageManager } from './types.js'

/**
 * Validation utilities using Zod schemas for type-safe input validation.
 *
 * Provides security-focused validation for user inputs including project names,
 * paths, and configuration values. All validators return Result types for
 * explicit error handling.
 *
 * @module config/validation
 */

// Constants for validation limits
const MAX_PROJECT_NAME_LENGTH = 100
const MAX_PATH_LENGTH = 260 // Windows MAX_PATH limit
const MAX_TEXT_LENGTH = 1000

// Allowed values
const ALLOWED_PACKAGE_MANAGERS = ['npm', 'pnpm'] as const

// Zod schemas for validation
const projectNameSchema = z
  .string()
  .min(1, 'Project name cannot be empty')
  .max(
    MAX_PROJECT_NAME_LENGTH,
    `Project name must be ${MAX_PROJECT_NAME_LENGTH} characters or less`
  )
  .regex(
    /^[a-zA-Z0-9][a-zA-Z0-9._-]*$/,
    'Project name can only contain alphanumeric characters, dots, dashes, and underscores. Must start with alphanumeric character.'
  )
  .refine(
    (name: string) => {
      const reservedNames = [
        'con',
        'prn',
        'aux',
        'nul',
        'com1',
        'com2',
        'com3',
        'com4',
        'com5',
        'com6',
        'com7',
        'com8',
        'com9',
        'lpt1',
        'lpt2',
        'lpt3',
        'lpt4',
        'lpt5',
        'lpt6',
        'lpt7',
        'lpt8',
        'lpt9',
      ]
      return !reservedNames.includes(name.toLowerCase())
    },
    {
      message: 'Reserved name cannot be used',
    }
  )

const packageManagerSchema = z.enum(ALLOWED_PACKAGE_MANAGERS)

const pathSchema = z
  .string()
  .min(1, 'Path cannot be empty')
  .max(MAX_PATH_LENGTH, `Path must be ${MAX_PATH_LENGTH} characters or less`)
  .refine((path: string) => !path.includes('\u0000'), 'Path contains null bytes')

const textSchema = z
  .string()
  .max(MAX_TEXT_LENGTH, `Text input must be ${MAX_TEXT_LENGTH} characters or less`)
  .transform((text: string) => {
    // Remove dangerous characters
    let sanitized = text
    // eslint-disable-next-line no-control-regex
    sanitized = sanitized.replace(/\u0000/g, '') // null bytes
    // eslint-disable-next-line no-control-regex
    sanitized = sanitized.replace(/[\u0001-\u0008]/g, '') // control chars
    // eslint-disable-next-line no-control-regex
    sanitized = sanitized.replace(/[\u000B\u000C]/g, '') // form feed, vertical tab
    // eslint-disable-next-line no-control-regex
    sanitized = sanitized.replace(/[\u000E-\u001F]/g, '') // other control chars
    sanitized = sanitized.replace(/\u007F/g, '') // delete char
    return sanitized.trim()
  })

/**
 * Helper function to convert Zod validation results to Result type
 */
function zodResultToResult<T>(
  schema: z.ZodSchema<T>,
  input: unknown,
  operation: string
): Result<T, CoreError> {
  try {
    const result = schema.parse(input)
    return ok(result)
  } catch (error) {
    if (error instanceof z.ZodError) {
      const firstIssue = error.issues[0]
      return err(
        createCoreError('VALIDATION_ERROR', 'CLI_ERROR', firstIssue.message, {
          context: { operation, details: `${operation} validation failed: ${firstIssue.message}` },
        })
      )
    }
    return err(
      createCoreError('VALIDATION_FAILED', 'CLI_ERROR', `${operation} validation failed`, {
        cause: error,
        context: { details: error instanceof Error ? error.message : String(error) },
      })
    )
  }
}

/**
 * Validate and sanitize a project name.
 *
 * Checks length, character restrictions (alphanumeric, dots, dashes, underscores),
 * and rejects Windows reserved names (CON, PRN, AUX, NUL, COM1-9, LPT1-9).
 *
 * @param name - Project name to validate
 * @returns Result with validated name or validation error
 */
export function validateProjectName(name: string): Result<string, CoreError> {
  return zodResultToResult(projectNameSchema, name, 'Project name')
}

/**
 * Validate project path with directory traversal protection.
 *
 * Resolves relative paths against baseDir and validates path length
 * and null byte restrictions for security.
 *
 * @param inputPath - Path to validate (absolute or relative)
 * @param baseDir - Base directory for resolving relative paths
 * @returns Result with resolved absolute path or validation error
 */
export function validateProjectPath(inputPath: string, baseDir: string): Result<string, CoreError> {
  // First validate the basic path schema
  const basicValidation = zodResultToResult(pathSchema, inputPath, 'Project path')
  if (!basicValidation.isOk()) {
    return basicValidation
  }

  const trimmed = inputPath.trim()

  try {
    const resolvedPath = isAbsolute(trimmed) ? trimmed : resolve(baseDir, trimmed)
    return ok(resolvedPath)
  } catch (error) {
    return err(
      createCoreError('INVALID_PROJECT_PATH', 'CLI_ERROR', 'Invalid project path', {
        cause: error,
        context: { details: 'Unable to resolve project path' },
      })
    )
  }
}

/**
 * Validate package manager selection.
 *
 * Only allows whitelisted package managers: npm, pnpm.
 *
 * @param packageManager - Package manager name to validate
 * @returns Result with validated package manager or validation error
 */
export function validatePackageManager(packageManager: string): Result<string, CoreError> {
  return zodResultToResult(packageManagerSchema, packageManager, 'Package manager')
}

/**
 * Validate template file path to prevent directory traversal attacks.
 *
 * Ensures the resolved path stays within the base template directory.
 * Rejects paths with ".." sequences or absolute paths that escape.
 *
 * @param filePath - Template file path to validate
 * @param baseTemplateDir - Base directory templates must reside within
 * @returns Result with resolved safe path or validation error
 */
export function validateTemplatePath(
  filePath: string,
  baseTemplateDir: string
): Result<string, CoreError> {
  // First validate basic path
  const basicValidation = zodResultToResult(pathSchema, filePath, 'Template file path')
  if (!basicValidation.isOk()) {
    return basicValidation
  }

  try {
    const normalizedPath = normalize(filePath)
    const resolvedPath = resolve(baseTemplateDir, normalizedPath)
    const relativePath = relative(baseTemplateDir, resolvedPath)

    if (relativePath.startsWith('..') || isAbsolute(relativePath)) {
      return err(
        createCoreError('INVALID_TEMPLATE_PATH', 'CLI_ERROR', 'Invalid template path', {
          context: { details: 'Template path must be within the template directory' },
        })
      )
    }

    return ok(resolvedPath)
  } catch (error) {
    return err(
      createCoreError('INVALID_TEMPLATE_PATH', 'CLI_ERROR', 'Invalid template path', {
        cause: error,
        context: { details: 'Unable to resolve template path' },
      })
    )
  }
}

/**
 * Validate output file path to prevent directory traversal attacks.
 *
 * Ensures generated files are written only within the project directory.
 * Rejects paths with ".." or absolute paths that would escape the project.
 *
 * @param filePath - Output file path to validate (must be relative)
 * @param baseOutputDir - Project directory files must be written within
 * @returns Result with resolved safe path or validation error
 */
export function validateOutputPath(
  filePath: string,
  baseOutputDir: string
): Result<string, CoreError> {
  // First validate basic path
  const basicValidation = zodResultToResult(pathSchema, filePath, 'Output file path')
  if (!basicValidation.isOk()) {
    return basicValidation
  }

  try {
    const normalizedPath = normalize(filePath)

    if (normalizedPath.includes('..') || isAbsolute(normalizedPath)) {
      return err(
        createCoreError('INVALID_OUTPUT_PATH', 'CLI_ERROR', 'Invalid output path', {
          context: { details: 'Output path must be relative and within the project directory' },
        })
      )
    }

    const resolvedPath = resolve(baseOutputDir, normalizedPath)
    const relativePath = relative(baseOutputDir, resolvedPath)

    if (relativePath.startsWith('..') || isAbsolute(relativePath)) {
      return err(
        createCoreError('INVALID_OUTPUT_PATH', 'CLI_ERROR', 'Invalid output path', {
          context: { details: 'Output path must be within the project directory' },
        })
      )
    }

    return ok(resolvedPath)
  } catch (error) {
    return err(
      createCoreError('INVALID_OUTPUT_PATH', 'CLI_ERROR', 'Invalid output path', {
        cause: error,
        context: { details: 'Unable to resolve output path' },
      })
    )
  }
}

/**
 * Sanitize text input to prevent injection attacks.
 *
 * Removes null bytes, control characters, and other dangerous characters.
 * Trims whitespace and enforces length limits.
 *
 * @param input - Text string to sanitize
 * @param _maxLength - Maximum allowed length (default: 1000, currently unused)
 * @returns Result with sanitized text or validation error
 */
export function sanitizeText(
  input: string,
  _maxLength: number = MAX_TEXT_LENGTH
): Result<string, CoreError> {
  if (!input || typeof input !== 'string') {
    return err(
      createCoreError('TEXT_INPUT_REQUIRED', 'CLI_ERROR', 'Text input is required', {
        context: { details: 'Text input must be a string' },
      })
    )
  }

  return zodResultToResult(textSchema, input, 'Text input')
}

/**
 * Validate and normalize project configuration with defaults.
 *
 * Takes a partial configuration and returns a complete ProjectConfig with
 * all required fields validated and sensible defaults applied for optional fields.
 *
 * @param config - Partial configuration with at least projectName
 * @returns Result with complete validated ProjectConfig or validation error
 *
 * @remarks
 * Default values applied:
 * - packageManager: 'pnpm'
 * - projectType: 'standalone-cli'
 * - nodeVersion: '18'
 * - typescript: true
 * - features.testing: true
 */
export function validateConfig(config: Partial<ProjectConfig>): Result<ProjectConfig, CoreError> {
  try {
    // Validate required fields
    if (!config.projectName) {
      return err(
        createCoreError('MISSING_PROJECT_NAME', 'CLI_ERROR', 'Project name is required', {
          component: 'ConfigValidator',
          operation: 'validateConfig',
          recoverable: true,
          severity: 'high',
          suggestion: 'Provide a project name',
        })
      )
    }

    // Validate project name
    const nameValidation = validateProjectName(config.projectName)
    if (nameValidation.isErr()) {
      return err(nameValidation.error)
    }

    // Validate and set project path
    const projectPath = config.projectPath || resolve(process.cwd(), config.projectName)
    const pathValidation = validateProjectPath(projectPath, process.cwd())
    if (pathValidation.isErr()) {
      return err(pathValidation.error)
    }

    // Validate package manager
    const packageManager = (config.packageManager || 'pnpm') as PackageManager
    const pmValidation = validatePackageManager(packageManager)
    if (pmValidation.isErr()) {
      return err(pmValidation.error)
    }

    // Create complete configuration with defaults
    const validatedConfig: ProjectConfig = {
      projectName: nameValidation.value,
      projectPath: pathValidation.value,
      packageManager: pmValidation.value as PackageManager,
      features: {
        core: true, // Always required
        config: config.features?.config ?? false,
        validation: config.features?.validation ?? false,
        testing: config.features?.testing ?? true,
        cicd: config.features?.cicd ?? false,
      },
      projectType: config.projectType || 'standalone-cli',
      nodeVersion: config.nodeVersion || '18',
      typescript: config.typescript ?? true, // Always true for this generator
      ide: config.ide || 'none',
      dryRun: config.dryRun ?? false,
      force: config.force ?? false,
      verbose: config.verbose ?? false,
    }

    return ok(validatedConfig)
  } catch (error) {
    return err(
      createCoreError('CONFIG_VALIDATION_ERROR', 'CLI_ERROR', 'Configuration validation failed', {
        component: 'ConfigValidator',
        operation: 'validateConfig',
        cause: error,
        recoverable: false,
        severity: 'high',
      })
    )
  }
}
